import { CliError, EXIT_USAGE } from "../../../common.js";
import {
  listActionLocationsV4,
  listEpisodeLocationsV4,
  listSceneLocationsV4,
  parseV4Address,
  type V4Address,
} from "./address.js";
import { resolveV4Ordinal } from "./ordinal.js";
import type { ScriptV4 } from "./types.js";

type OrderedKind = "episode" | "scene" | "action";

export interface V4Selector {
  readonly isEmpty: boolean;
  readonly isWholeScript: boolean;
  matchesEpisode(id: string): boolean;
  matchesScene(id: string): boolean;
  matchesAction(id: string): boolean;
}

function selectorError(message: string, received: string): never {
  throw new CliError(message, {
          exitCode: EXIT_USAGE,
          errorCode: "V4_SELECTOR_INVALID",
          data: { expected: ["typed v4 ID or same-kind inclusive range; comma joins regions"], actual: [received] },
        });
}

function orderedKind(address: V4Address, raw: string): OrderedKind {
  if (address.kind === "episode" || address.kind === "scene" || address.kind === "action") return address.kind;
  return selectorError("Asset and state addresses are not narrative range anchors.", raw);
}

function addressId(address: V4Address): string {
  if (address.kind === "state") return `${address.assetId}/${address.stateId}`;
  return address.id;
}

function resolveAnchor(script: ScriptV4, raw: string, allowOrdinals: boolean): V4Address {
  if (raw.startsWith("@")) {
    if (!allowOrdinals) selectorError("Ordinal anchors are query-only.", raw);
    return resolveV4Ordinal(script, raw);
  }
  return parseV4Address(raw);
}

function between(ids: readonly string[], left: string, right: string, raw: string): string[] {
  const leftIndex = ids.indexOf(left);
  const rightIndex = ids.indexOf(right);
  if (leftIndex < 0 || rightIndex < 0) selectorError("Range endpoint does not exist in the current script.", raw);
  const start = Math.min(leftIndex, rightIndex);
  const end = Math.max(leftIndex, rightIndex);
  return ids.slice(start, end + 1);
}

export function parseV4Selector(
  script: ScriptV4,
  rawValue: unknown,
  options: { allowOrdinals: boolean },
): V4Selector {
  const raw = typeof rawValue === "string" ? rawValue.trim() : "";
  if (!raw) {
    return {
      isEmpty: true,
      isWholeScript: false,
      matchesEpisode: () => true,
      matchesScene: () => true,
      matchesAction: () => true,
    };
  }
  if (raw === "*") {
    return {
      isEmpty: false,
      isWholeScript: true,
      matchesEpisode: () => true,
      matchesScene: () => true,
      matchesAction: () => true,
    };
  }

  const episodeRows = listEpisodeLocationsV4(script);
  const sceneRows = listSceneLocationsV4(script);
  const actionRows = listActionLocationsV4(script);
  const episodeIds = episodeRows.map((row) => row.episode.episode_id);
  const sceneIds = sceneRows.map((row) => row.scene.scene_id);
  const actionIds = actionRows.map((row) => row.action.action_id);
  const selectedEpisodes = new Set<string>();
  const selectedScenes = new Set<string>();
  const selectedActions = new Set<string>();

  const includeEpisode = (id: string): void => {
    selectedEpisodes.add(id);
    const episode = episodeRows.find((row) => row.episode.episode_id === id)?.episode;
    if (!episode) return;
    episode.scenes.forEach((scene) => {
      selectedScenes.add(scene.scene_id);
      scene.actions.forEach((action) => selectedActions.add(action.action_id));
    });
  };
  const includeScene = (id: string): void => {
    selectedScenes.add(id);
    const row = sceneRows.find((candidate) => candidate.scene.scene_id === id);
    if (!row) return;
    selectedEpisodes.add(row.episode.episode_id);
    row.scene.actions.forEach((action) => selectedActions.add(action.action_id));
  };
  const includeAction = (id: string): void => {
    selectedActions.add(id);
    const row = actionRows.find((candidate) => candidate.action.action_id === id);
    if (!row) return;
    selectedEpisodes.add(row.episode.episode_id);
    selectedScenes.add(row.scene.scene_id);
  };

  for (const segmentValue of raw.split(",")) {
    const segment = segmentValue.trim();
    if (!segment) selectorError("Selector contains an empty region.", raw);
    const parts = segment.split("..");
    if (parts.length > 2) selectorError("Selector region has too many range delimiters.", segment);
    const left = resolveAnchor(script, parts[0]!.trim(), options.allowOrdinals);
    const right = resolveAnchor(script, (parts[1] ?? parts[0])!.trim(), options.allowOrdinals);
    const kind = orderedKind(left, segment);
    if (orderedKind(right, segment) !== kind) selectorError("Range endpoints must have the same kind.", segment);
    const leftId = addressId(left);
    const rightId = addressId(right);
    const ids = kind === "episode" ? episodeIds : kind === "scene" ? sceneIds : actionIds;
    const selected = between(ids, leftId, rightId, segment);
    if (kind === "episode") selected.forEach(includeEpisode);
    else if (kind === "scene") selected.forEach(includeScene);
    else selected.forEach(includeAction);
  }

  return {
    isEmpty: false,
    isWholeScript: false,
    matchesEpisode: (id) => selectedEpisodes.has(id),
    matchesScene: (id) => selectedScenes.has(id),
    matchesAction: (id) => selectedActions.has(id),
  };
}
